export class StorageService {
    private readonly storageProvider: Storage;

    public readonly storageType: 'session' | 'local';

    constructor(useSessionStorage?: boolean) {
        //handle storage providers
        if (useSessionStorage) {
            this.storageProvider = window.sessionStorage;
            this.storageType = 'session';
        } else {
            this.storageProvider = window.localStorage;
            this.storageType = 'local';
        }
    }

    /**
     * Saves a provided value to storage
     * @param key - Key to store the value at
     * @param value - Value to store
     * @returns Boolean indicating success of the save
     */
    set<T>(key: string, value: T): boolean {
        try {
            this.storageProvider.setItem(key, JSON.stringify(value));
        } catch (error) {
            return false;
        }
        return true;
    }

    /**
     * Retrieve the value stored at a specific key in the original base type it was saved as.
     * @remarks
     * Verify type coming back matches the type expected
     * @param key - Key to retrieve value from
     * @returns Parsed version of the string saved in Storage or null if parse fails or types do not match
     */
    get<T>(key: string): T | null {
        const res = this.storageProvider.getItem(key);
        if (res) {
            return JSON.parse(res) as T;
        } else {
            return null;
        }
    }

    /**
     * Set a property of a specified name to an object at or nested in another object at the key provided in Storage
     * @param key - Key of root object in Storage
     * @param nestedPath - Path to a nested object to modify ([] if no nesting present in use case)
     * @param propertyName - Property name to set on the object
     * @param value - Value of property to set on the object
     * @returns Returns boolean indicating success of the property setting
     */
    setPropertyOfObject(key: string, nestedPath: string[], propertyName: string, value: any): boolean {
        //get the object at the provided key and ensure it is real and an object
        let objAtKey = this.get<any>(key);
        if (!objAtKey || typeof objAtKey !== 'object') {
            objAtKey = {};
        }
        //get a nested obj out of the object for modification via the path provided through the object hierarchy
        let currentPlace = objAtKey;
        for (const p of nestedPath) {
            currentPlace = currentPlace[p];
            if (!currentPlace) {
                return false;
            }
        }
        const objToModify = currentPlace;
        objToModify[propertyName] = value;
        return this.set(key, objAtKey);
    }

    /**
     * Modifies an array at the key or nested in an object at the specified key at the specified nestedObjKey to have the value provided appended to it
     * @param key - Key that the object is stored in in Storage
     * @param nestedPath - Path to the array through the object hierarchy. Must end with property name of the array to modify or empty to modify an array at the provided storage key
     * @param value - Value to be appended to the array
     * @returns Boolean value indicating success of appending
     */
    appendToArray(key: string, nestedPath: string[], value: any): boolean {
        //get the object at the provided key and ensure it is real and an object
        let objAtKey = this.get<any>(key);
        if (!objAtKey || typeof objAtKey !== 'object')  {
            //when not attempting to nest in an existing object, default to an empty array
            //if nesting, just exit as this is clealy not the structure expected
            if (nestedPath.length === 0) {
                objAtKey = [];
            } else {
                return false;
            }
            
        }
        //get the array out of the object for modification via the path provided through the object hierarchy
        let currentPlace = objAtKey;
        for (const p of nestedPath) {
            currentPlace = currentPlace[p];
            if (!currentPlace) {
                return false;
            }
        }
        const arr = currentPlace;
        if (!arr || !Array.isArray(arr)) {
            return false;
        }
        //add new value and save obj to Storage
        arr.push(value);
        return this.set(key, objAtKey);
    }

    /**
     * Removes a value from an array at the key provided or nested in an object stored in Storage
     * @param key - Key the object is stored in in Storage
     * @param nestedPath - Path to the array through the object hierarchy. Must end with property name of the array to modify or empty if array is stored at the key in storage
     * @param value - Value to remove from the array
     * @returns Boolean indicating success of removal
     */
    removeFromArray(key: string, nestedPath: string[], value: any): boolean {
        //get the object at the provided key and ensure it is real and an object
        const objAtKey = this.get<any>(key);
        if (!objAtKey || typeof objAtKey !== 'object') {
            return false;
        }
        //get the array out of the object for modification via the path provided through the object hierarchy
        let currentPlace = objAtKey;
        for (const p of nestedPath) {
            currentPlace = currentPlace[p];
            if (!currentPlace) {
                return false;
            }
        }
        let arr = currentPlace;
        if (!arr || !Array.isArray(arr)) {
            return false;
        }
        //ensure an element matching the specified value is actually present
        const elementIndex = arr.findIndex(item => item === value);
        if (elementIndex === -1) {
            return false;
        }
        //remove the found element and save it to storage
        arr = arr.splice(elementIndex, 1);
        return this.set(key, objAtKey);
    }
}



// WEBPACK FOOTER //
// ./src/services/storage-service.ts